import PropTypes from "prop-types"
import { isNumber } from "lodash"
import { Box, Typography, IconButton, Tooltip, Alert } from "@mui/material"
import { DataGridPremium as DataGrid, useGridApiRef, GridColumnMenu } from "@mui/x-data-grid-premium"
import { Download as DownloadIcon, ThumbUp, ThumbDown, ThumbUpOffAlt, ThumbDownOffAlt, ContentCopy } from "@mui/icons-material"
import Ellipsable from "components/ellipsable"
import { useState, useCallback, useMemo } from "react"
import { ErrorBoundary } from "@sentry/react"
import { useResourceCurrency } from "resources/currencies"
import { useAnalytics } from "connected-components/analytics"
import { QUICK_ANSWERS_EVENTS } from "../analytics"
import LastUpdated from "./last-updated"
import FeedbackForm from "components/feedback-form"
import { MAX_PIVOT_COLUMNS, VALUE_FORMATS } from "helpers/ai"
import { formatValueForDisplay, parseValue, isNumberFormat, getColumnType } from "../utils/formatting"
import TableGenerationSteps from "./table-generation-steps"
import { MODEL_METADATA_SHAPE, INSTRUMENTS_WITH_METADATA_SHAPE } from "connected-components/asset-view/prop-types"
import { isPivotable } from "../utils"

export default function QuickAnswersResults({
  results,
  lastUpdated,
  question,
  hasAskedQuestion = false,
  isLoading,
  modelMetadata,
  original,
}) {
  const analytics = useAnalytics()
  const [currency] = useResourceCurrency(modelMetadata?.id, original)
  const apiRef = useGridApiRef()
  const [pivotWarning, setPivotWarning] = useState(null)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [thumb, setThumb] = useState(null)
  const [copySuccess, setCopySuccess] = useState(false)

  const hasRecords = results?.records?.length > 0
  const recordCount = results?.records?.length
  const firstRecord = results?.records?.[0]
  const hasColumnMetadata = results?.columns?.length > 0

  const columnHeaders = useMemo(() => {
    if (hasColumnMetadata) {
      return results.columns.map(col => col.label)
    }
    return firstRecord ? Object.keys(firstRecord) : []
  }, [firstRecord, hasColumnMetadata, results?.columns])

  const columnFormats = useMemo(() => {
    return columnHeaders.reduce((acc, key, index) => {
      const fieldName = `col_${index}`
      if (hasColumnMetadata) {
        const columnMetadata = results.columns[index]
        acc[fieldName] = columnMetadata.format
      } else {
        acc[fieldName] = isNumber(firstRecord?.[key]) ? VALUE_FORMATS.NUMBER : VALUE_FORMATS.STRING
      }
      return acc
    }, {})
  }, [columnHeaders, hasColumnMetadata, results?.columns, firstRecord])

  const columns = useMemo(() => {
    return columnHeaders.map((headerName, index) => {
      const fieldName = `col_${index}`
      const format = columnFormats[fieldName]
      const isNumericColumn = isNumberFormat(format)

      return {
        field: fieldName,
        headerName,
        type: getColumnType(format),
        flex: 1,
        minWidth: isNumericColumn ? 150 : 120,
        valueGetter: (value) => parseValue(value, format),
        valueFormatter: (value) => formatValueForDisplay(value, format, currency),
        hideable: true,
        exportable: true,
        pivotable: isPivotable(headerName, results?.records),
        disableReorder: false,
      }
    })
  }, [columnHeaders, results?.records, columnFormats, currency])

  // Add id field for DataGrid - use integer keys for all data
  const rows = useMemo(() => {
    if (!hasRecords) return []

    return results.records.map((record, rowIndex) => {
      // Build clean record with integer id and integer-based field names using reduce
      return columnHeaders.reduce((mappedRecord, columnHeader, colIndex) => {
        const fieldName = `col_${colIndex}`
        mappedRecord[fieldName] = record[columnHeader]
        return mappedRecord
      }, { id: rowIndex })
    })
  }, [hasRecords, results?.records, columnHeaders])

  const handleExport = useCallback(() => {
    if (!hasRecords || !apiRef.current) return

    // Sanitize question for filename - remove invalid characters and limit length
    const sanitizedQuestion = question
      .replace(/[<>:"/\\|?*]/g, "") // Remove invalid filename characters
      .replace(/\./g, ",") // Replace periods with commas
      .substring(0, 100) // Limit length to 100 characters
      .replace(/-+$/, "") // Remove trailing hyphens

    const fileName = sanitizedQuestion || `quick-answers-results-${Date.now()}`

    apiRef.current.exportDataAsExcel({
      fileName,
      includeHeaders: true,
      getRowsToExport: () => apiRef.current.getAllRowIds(),
    })

    analytics.track(QUICK_ANSWERS_EVENTS.XLSX_DOWNLOAD, { question, recordCount })
  }, [hasRecords, apiRef, question, analytics, recordCount])

  const handleCopy = useCallback(() => {
    // Convert results to TSV format for copying (works better with spreadsheet programs)
    const tsvContent = [
      columnHeaders.join("\t"),
      ...(results?.records || []).map(row =>
        columnHeaders.map((headerName, index) => {
          const fieldName = `col_${index}`
          const format = columnFormats[fieldName]
          const value = parseValue(row[headerName], format)
          if (value === null || value === undefined) {
            return ""
          }
          return String(value).replace(/\t/g, " ").replace(/\n/g, " ")
        }).join("\t"),
      ),
    ].join("\n")

    navigator.clipboard.writeText(tsvContent).then(() => {
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)

      analytics.track(QUICK_ANSWERS_EVENTS.COPY_CLICKED, { question })
    })
  }, [columnHeaders, results?.records, analytics, question, columnFormats])

  const thumbsClick = useCallback((direction) => () => {
    if (thumb === direction) {
      return
    }

    analytics.track(QUICK_ANSWERS_EVENTS.FEEDBACK_GIVEN, { feedback: direction, question })

    setThumb(direction)
    setFeedbackOpen(true)
  }, [thumb, question, analytics])

  const handlePivotModelChange = (model) => {
    const pivotColumns = model?.columns || []
    if (pivotColumns.length > MAX_PIVOT_COLUMNS) {
      setPivotWarning(`Too many pivot columns selected. Maximum allowed is ${MAX_PIVOT_COLUMNS}.`)
      // Reset to previous state
      apiRef.current.setPivotModel({
        ...model,
        columns: pivotColumns.slice(0, MAX_PIVOT_COLUMNS),
      })
    } else {
      setPivotWarning(null)
    }
  }

  const hasColumns = columnHeaders.length > 0

  if (!hasAskedQuestion || isLoading || (!hasColumns && !results?.explain_steps)) {
    return null
  }

  const CustomColumnMenu = (props) => {
    return (
      <GridColumnMenu
        {...props}
        slots={{
          columnMenuAggregationItem: null,
          columnMenuGroupingItem: null,
        }}
      />
    )
  }

  const feedbackTrackingProps = { question, direction: thumb }

  const CustomFooter = () => {
    return (
      <Box className="data-grid-footer">
        <Box className="footer-actions">
          <Tooltip title="Helpful answer">
            <IconButton
              size="small"
              onClick={thumbsClick("positive")}
              color={thumb === "positive" ? "primary" : "default"}
            >
              {thumb === "positive" ? <ThumbUp /> : <ThumbUpOffAlt />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Unhelpful answer">
            <IconButton
              size="small"
              onClick={thumbsClick("negative")}
              color={thumb === "negative" ? "primary" : "default"}
            >
              {thumb === "negative" ? <ThumbDown /> : <ThumbDownOffAlt />}
            </IconButton>
          </Tooltip>
          <Tooltip title={copySuccess ? "Copied!" : "Copy to clipboard"}>
            <IconButton
              size="small"
              onClick={handleCopy}
              color={copySuccess ? "success" : "default"}
            >
              <ContentCopy />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {results.records.length} row{results.records.length !== 1 ? "s" : ""}
        </Typography>
      </Box>
    )
  }

  return <Box className="results">
    <Box className="results-header">
      <Box className="header-content">
        <Ellipsable width="100%" className="question-text">
          {question}
        </Ellipsable>
        {hasRecords && (
          <Tooltip title="Download as XLSX">
            <IconButton onClick={handleExport} size="small">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box className="last-updated">
        <LastUpdated timestamp={lastUpdated} staleThresholdDays={1} />
      </Box>
    </Box>
    <TableGenerationSteps
      explanation={results?.explain_steps}
      question={question}
    />
    {pivotWarning && (
      <Alert severity="warning" className="pivot-warning">
        {pivotWarning}
      </Alert>
    )}
    {hasColumns && (
      <Box className="data-grid-container">
        <ErrorBoundary fallback={<div className="grid-error-boundary">⚠️ Failed to load the grid.</div>}>
          <DataGrid
            apiRef={apiRef}
            rows={rows}
            columns={columns}
            density="compact"
            slots={{
              columnMenu: CustomColumnMenu,
              footer: CustomFooter,
            }}
            onPivotModelChange={handlePivotModelChange}
            className="data-grid"
            localeText={{
              noRowsLabel: "There are no results that matched your criteria",
            }}
          />
        </ErrorBoundary>
      </Box>
    )}
    <FeedbackForm
      open={feedbackOpen}
      onClose={() => setFeedbackOpen(false)}
      feedbackTrackingProps={feedbackTrackingProps}
      onAdditionalFeedback={(feedback) => analytics.track(QUICK_ANSWERS_EVENTS.ADDITIONAL_FEEDBACK_GIVEN, { feedback, question })}
      getPlaceholderText={(direction) => `Why was this answer ${direction === "positive" ? "helpful" : "unhelpful"}?`}
    />
  </Box>
}

QuickAnswersResults.propTypes = {
  results: PropTypes.object,
  lastUpdated: PropTypes.instanceOf(Date),
  question: PropTypes.string.isRequired,
  hasAskedQuestion: PropTypes.bool,
  isLoading: PropTypes.bool,
  modelMetadata: MODEL_METADATA_SHAPE,
  original: INSTRUMENTS_WITH_METADATA_SHAPE.original,
}
